import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FormRowGrid, GridRowProps} from '../../../../shared/GridRow';
import {QuoteFormControlElement} from '../../../../shared/QuoteFormControlElement';
import {QuoteFormElement} from '../../../../shared/QuoteFormElement';
import {FormWrapper} from '../../../../shared/QuoteFormWrapper';
import {unitsOptions} from '../../PlanTemplates/ACC/benefitDefaults';
import {formatCurrencyValue} from './ACCBenefits';
import {accidentAmbulanceCarePath, getAccidentXRayPath} from './PropertyPaths/propertyPathACC';

// Urgent Care Component
const UrgentCare = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan, selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);
  const productCd = selectedProduct?.code ?? ''

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'urgent-care-row-1'},
      items: [
        {
          key: 'urgent-care-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.urgentCareDetails.incurralPeriod)}
              id={propertyPaths.urgentCareDetails.incurralPeriod}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'customize-benefit',
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              checked={getQuoteFieldValue<boolean>(propertyPaths.urgentCareDetails.customizeBenefitAmount)}
              id={propertyPaths.urgentCareDetails.customizeBenefitAmount}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'urgent-care-row-2'},
      items: [
        {
          key: 'urgent-care-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              adornment="money"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.urgentCareDetails.benefitAmount))}
              id={propertyPaths.urgentCareDetails.benefitAmount}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'urgent-care-payable-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`${productCd}AmbulanceUrgentCareBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.urgentCareDetails.maxBenefitNumber)}
              id={propertyPaths.urgentCareDetails.maxBenefitNumber}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'urgent-care-row-3'},
      items: [
        {
          key: 'urgent-care-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}AmbulanceUrgentCareBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.urgentCareDetails.timePeriodCd)}
              id={propertyPaths.urgentCareDetails.timePeriodCd}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];
  return <FormRowGrid rows={rows} />;
};

// Accident Physician Treatment Component
const AccidentPhysicianTreatment = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan, selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);
  const productCd = selectedProduct?.code ?? ''

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'accident-physician-treatment-row-1'},
      items: [
        {
          key: 'accident-physician-treatment-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceUrgentCareBenefit.incurralPeriod)}
              id={propertyPaths.ambulanceUrgentCareBenefit.incurralPeriod}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'customize-benefit',
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              checked={getQuoteFieldValue<boolean>(propertyPaths.ambulanceUrgentCareBenefit.customizeBenefitAmount)}
              id={propertyPaths.ambulanceUrgentCareBenefit.customizeBenefitAmount}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'accident-physician-treatment-row-2'},
      items: [
        {
          key: 'accident-physician-treatment-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              adornment="money"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.ambulanceUrgentCareBenefit.benefitAmount)
              )}
              id={propertyPaths.ambulanceUrgentCareBenefit.benefitAmount}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'accident-physician-treatment-payable-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`${productCd}AmbulanceUrgentCareBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceUrgentCareBenefit.maxBenefitNumber)}
              id={propertyPaths.ambulanceUrgentCareBenefit.maxBenefitNumber}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'accident-physician-treatment-row-3'},
      items: [
        {
          key: 'accident-physician-treatment-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Time Period"
              searchKey={`${productCd}AmbulanceUrgentCareBenefit.timePeriodCd`}
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceUrgentCareBenefit.timePeriodCd)}
              id={propertyPaths.ambulanceUrgentCareBenefit.timePeriodCd}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

// Air Ambulance Component
const AirAmbulance = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan, selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);
  const productCd = selectedProduct?.code ?? ''

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'air-ambulance-row-1'},
      items: [
        {
          key: 'air-ambulance-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceAirDetails.incurralPeriod)}
              id={propertyPaths.ambulanceAirDetails.incurralPeriod}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'customize-benefit',
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              checked={getQuoteFieldValue<boolean>(propertyPaths.ambulanceAirDetails.customizeBenefitAmount)}
              id={propertyPaths.ambulanceAirDetails.customizeBenefitAmount}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'air-ambulance-row-2'},
      items: [
        {
          key: 'air-ambulance-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.ambulanceAirDetails.benefitAmount))}
              id={propertyPaths.ambulanceAirDetails.benefitAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'air-ambulance-payable-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`${productCd}AmbulanceAirBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceAirDetails.maxBenefitNumber)}
              id={propertyPaths.ambulanceAirDetails.maxBenefitNumber}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'air-ambulance-row-3'},
      items: [
        {
          key: 'air-ambulance-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}AmbulanceAirBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceAirDetails.timePeriodCd)}
              id={propertyPaths.ambulanceAirDetails.timePeriodCd}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

// Ground Ambulance Component
const GroundAmbulance = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan, selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);
  const productCd = selectedProduct?.code ?? ''

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'ground-ambulance-row-1'},
      items: [
        {
          key: 'ground-ambulance-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceGroundOrWaterDetails.incurralPeriod)}
              id={propertyPaths.ambulanceGroundOrWaterDetails.incurralPeriod}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'customize-benefit',
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              checked={getQuoteFieldValue<boolean>(propertyPaths.ambulanceGroundOrWaterDetails.customizeBenefitAmount)}
              id={propertyPaths.ambulanceGroundOrWaterDetails.customizeBenefitAmount}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ground-ambulance-row-2'},
      items: [
        {
          key: 'ground-ambulance-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.ambulanceGroundOrWaterDetails.benefitAmount)
              )}
              id={propertyPaths.ambulanceGroundOrWaterDetails.benefitAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'ground-ambulance-payable-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`${productCd}AmbulanceGroundOrWaterBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceGroundOrWaterDetails.maxBenefitNumber)}
              id={propertyPaths.ambulanceGroundOrWaterDetails.maxBenefitNumber}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'ground-ambulance-row-3'},
      items: [
        {
          key: 'ground-ambulance-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}AmbulanceGroundOrWaterBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.ambulanceGroundOrWaterDetails.timePeriodCd)}
              id={propertyPaths.ambulanceGroundOrWaterDetails.timePeriodCd}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

// X-Ray Component
const XRay = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan, selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);
  const productCd = selectedProduct?.code ?? ''

  const xRayPropertyPaths = getAccidentXRayPath(selectedPlan?.planIndex || 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'xray-row-1'},
      items: [
        {
          key: 'xray-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(xRayPropertyPaths.xRayDetails.incurralPeriod)}
              id={xRayPropertyPaths.xRayDetails.incurralPeriod}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'customize-benefit',
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              disabled
              checked={getQuoteFieldValue<boolean>(xRayPropertyPaths.xRayDetails.customizeBenefitAmount)}
              id={xRayPropertyPaths.xRayDetails.customizeBenefitAmount}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'xray-row-2'},
      items: [
        {
          key: 'xray-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={formatCurrencyValue(getQuoteFieldValue<number>(xRayPropertyPaths.xRayDetails.benefitAmount))}
              id={xRayPropertyPaths.xRayDetails.benefitAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'xray-payable-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`${productCd}XRayBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(xRayPropertyPaths.xRayDetails.maxBenefitNumber)}
              id={xRayPropertyPaths.xRayDetails.maxBenefitNumber}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'xray-row-3'},
      items: [
        {
          key: 'xray-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}XRayBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(xRayPropertyPaths.xRayDetails.timePeriodCd)}
              id={xRayPropertyPaths.xRayDetails.timePeriodCd}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const AccidentTreatmentUrgentCareBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentAmbulanceCarePath(selectedPlan?.planIndex || 0, 0);
  const xRayPropertyPaths = getAccidentXRayPath(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'accident-urgent-care-units-row'},
      items: [
        {
          key: 'units',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              type="number"
              options={unitsOptions}
              required
              value={getQuoteFieldValue<number>(propertyPaths.unitsOfAccAmbulanceUrgentCare)}
              id={propertyPaths.unitsOfAccAmbulanceUrgentCare}
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  const accidentTreatmentUrgentCareSections: ConditionalSection[] = [
    {
      Component: AccidentPhysicianTreatment,
      key: 'AccidentPhysicianTreatment',
      label: 'ACCIDENT PHYSICIAN TREATMENT',
      disabled: true,
      isApplied: getQuoteFieldValue<number>(propertyPaths.unitsOfAccAmbulanceUrgentCare) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isAccidentPhysicianTreatmentApplied, isApplied);
      },
    },
    {
      Component: AirAmbulance,
      key: 'AirAmbulance',
      label: 'AIR AMBULANCE',
      disabled: true,
      isApplied: getQuoteFieldValue<number>(propertyPaths.unitsOfAccAmbulanceUrgentCare) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isAmbulanceAirApplied, isApplied);
      },
    },
    {
      Component: GroundAmbulance,
      key: 'GroundAmbulance',
      label: 'GROUND AMBULANCE',
      disabled: true,
      isApplied: getQuoteFieldValue<number>(propertyPaths.unitsOfAccAmbulanceUrgentCare) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isAmbulanceGroundOrWaterApplied, isApplied);
      },
    },
    {
      Component: UrgentCare,
      key: 'UrgentCare',
      label: 'URGENT CARE',
      disabled: true,
      isApplied: getQuoteFieldValue<number>(propertyPaths.unitsOfAccAmbulanceUrgentCare) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(propertyPaths.isUrgentCareApplied, isApplied);
      },
    },
    {
      Component: XRay,
      key: 'XRay',
      label: 'X-RAY',
      disabled: true,
      isApplied: getQuoteFieldValue<number>(propertyPaths.unitsOfAccAmbulanceUrgentCare) > 1,
      onToggleVisibility: (isApplied: boolean) => {
        updateQuoteFieldValue(xRayPropertyPaths.isXRayApplied, isApplied);
      },
    },
  ];

  return (
    <FormWrapper title="Accident Treatment and Urgent Care Rider" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
        <QuoteFormConditionalSection sections={accidentTreatmentUrgentCareSections} />
      </Grid>
    </FormWrapper>
  );
};
